import {
  EdgeRoutes,
  RouteBox,
  RoutePoint,
  RouteSegment,
  RoutingEdge,
  RoutingObstacle,
} from './edge-routing.types'
import {
  manhattan,
  moveOutward,
  padBox,
  samePoint,
  segmentHitsBox,
  segmentLength,
  simplifyRoute,
  toSegments,
} from './route-geometry'

/*
 * Obstacle-aware orthogonal edge routing, run AFTER layout from the final node boxes, so routes
 * stay right whatever placed the nodes (an auto-arrange, a drag, a resize).
 *
 * For each edge, every "Z" or "U" shaped route through one channel (obstacle borders and
 * midpoints) is scored and the cheapest kept. Edges already routed are soft obstacles for the
 * later ones, so parallel edges spread out instead of stacking on one line. When no single-channel
 * route is clear of every node, an A* search over the obstacle-border grid finds a detour.
 *
 * Ported from the Archflow auto-layout care package (lib/routing.ts).
 */

/** Gap kept between a route and any node it passes. */
export const NODE_CLEARANCE = 16
/** How far a route runs straight out of a handle before it may turn. */
export const HANDLE_STUB = 28

// The ratios matter more than the numbers: running through a node is never acceptable, and one
// crossing is worth about 12k px of detour.
const COLLISION_PENALTY = 1_000_000
const CROSSING_PENALTY = 12_000
/** Per px of two routes sharing a line. */
const OVERLAP_PENALTY = 2_000
const BEND_PENALTY = 40
/** A route that runs past its stub and doubles back draws a hook at the arrowhead. */
const REVERSAL_PENALTY = 800
// Candidates grow with obstacles and scoring with routed segments; both are capped so a few
// hundred edges stay interactive
const MAX_CHANNEL_OBSTACLES = 12
const MAX_CONFLICT_SEGMENTS = 600

const distanceToBox = (point: RoutePoint, box: RouteBox): number =>
  (point.x < box.left ? box.left - point.x : point.x > box.right ? point.x - box.right : 0) +
  (point.y < box.top ? box.top - point.y : point.y > box.bottom ? point.y - box.bottom : 0)

/**
 * The obstacles whose borders become candidate channels: the ones nearest the edge, plus the four
 * outermost, so "go around everything" stays possible.
 */
function channelObstacles(obstacles: RouteBox[], a: RoutePoint, b: RoutePoint): RouteBox[] {
  if (obstacles.length <= MAX_CHANNEL_OBSTACLES) {
    return obstacles
  }
  const middle = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
  const nearby = [...obstacles]
    .sort((p, q) => distanceToBox(middle, p) - distanceToBox(middle, q))
    .slice(0, MAX_CHANNEL_OBSTACLES)
  const outermost = [
    obstacles.reduce((most, box) => (box.left < most.left ? box : most)),
    obstacles.reduce((most, box) => (box.right > most.right ? box : most)),
    obstacles.reduce((most, box) => (box.top < most.top ? box : most)),
    obstacles.reduce((most, box) => (box.bottom > most.bottom ? box : most)),
  ]

  return [...new Set([...nearby, ...outermost])]
}

/**
 * How far past the box spanning an edge's two ends a detour may look for nodes to go around. Two
 * columns of workflow cards either way leaves room for any sensible detour, while nodes further
 * away (which a detour never reaches) no longer grow its search grid.
 */
const DETOUR_MARGIN = 1_120
/**
 * How many grid points a detour search may visit before giving up. A route that exists turns up
 * within a few hundred; an end walled in by several cards would otherwise visit the whole grid,
 * on every live re-route while a node is dragged.
 */
const DETOUR_VISITS = 2_000

/**
 * The obstacles a detour between `a` and `b` can meet: those overlapping the padded box they span.
 * Exported so canvases running their own detour search bound it the same way, with a `margin`
 * sized to their nodes.
 */
export function obstaclesAround(
  obstacles: RouteBox[],
  a: RoutePoint,
  b: RoutePoint,
  margin = DETOUR_MARGIN
): RouteBox[] {
  const area = padBox(
    {
      left: Math.min(a.x, b.x),
      top: Math.min(a.y, b.y),
      right: Math.max(a.x, b.x),
      bottom: Math.max(a.y, b.y),
    },
    margin
  )

  return obstacles.filter(
    (box) =>
      box.left < area.right &&
      box.right > area.left &&
      box.top < area.bottom &&
      box.bottom > area.top
  )
}

/**
 * When a route runs out to a handle stub and straight back along the same line, the stub point is
 * dropped: same approach side, shorter stub, no hook at the arrowhead.
 */
function trimStubOvershoot(points: RoutePoint[]): RoutePoint[] {
  const overshoots = (a: RoutePoint, b: RoutePoint, c: RoutePoint): boolean =>
    (a.y === b.y && b.y === c.y && (b.x - a.x) * (c.x - b.x) < 0) ||
    (a.x === b.x && b.x === c.x && (b.y - a.y) * (c.y - b.y) < 0)
  const trimmed = [...points]
  while (
    trimmed.length > 3 &&
    overshoots(
      trimmed[trimmed.length - 3],
      trimmed[trimmed.length - 2],
      trimmed[trimmed.length - 1]
    )
  ) {
    trimmed.splice(trimmed.length - 2, 1)
  }
  while (trimmed.length > 3 && overshoots(trimmed[0], trimmed[1], trimmed[2])) {
    trimmed.splice(1, 1)
  }

  return trimmed
}

/** Every route through one vertical or one horizontal channel. */
function candidateRoutes(
  source: RoutePoint,
  sourceOut: RoutePoint,
  targetOut: RoutePoint,
  target: RoutePoint,
  obstacles: RouteBox[]
): RoutePoint[][] {
  const xs = new Set([sourceOut.x, targetOut.x, (sourceOut.x + targetOut.x) / 2])
  const ys = new Set([sourceOut.y, targetOut.y, (sourceOut.y + targetOut.y) / 2])
  obstacles.forEach((box) => {
    xs.add(box.left)
    xs.add(box.right)
    ys.add(box.top)
    ys.add(box.bottom)
  })

  return [
    ...[...xs].map((x) => [
      source,
      sourceOut,
      { x, y: sourceOut.y },
      { x, y: targetOut.y },
      targetOut,
      target,
    ]),
    ...[...ys].map((y) => [
      source,
      sourceOut,
      { x: sourceOut.x, y },
      { x: targetOut.x, y },
      targetOut,
      target,
    ]),
  ]
    .map(simplifyRoute)
    .map(trimStubOvershoot)
}

const isHorizontal = ({ start, end }: RouteSegment): boolean => start.y === end.y

/** Where two perpendicular segments cross; meeting at a shared end (one handle) is not a crossing. */
export function crossingPoint(a: RouteSegment, b: RouteSegment): RoutePoint | null {
  if (isHorizontal(a) === isHorizontal(b)) {
    return null
  }
  const horizontal = isHorizontal(a) ? a : b
  const vertical = isHorizontal(a) ? b : a
  const point = { x: vertical.start.x, y: horizontal.start.y }
  const inside =
    point.x >= Math.min(horizontal.start.x, horizontal.end.x) &&
    point.x <= Math.max(horizontal.start.x, horizontal.end.x) &&
    point.y >= Math.min(vertical.start.y, vertical.end.y) &&
    point.y <= Math.max(vertical.start.y, vertical.end.y)
  if (!inside) {
    return null
  }
  const endOf = (segment: RouteSegment): boolean =>
    samePoint(point, segment.start) || samePoint(point, segment.end)

  return endOf(a) && endOf(b) ? null : point
}

/** Length two collinear segments share. */
export function overlapLength(a: RouteSegment, b: RouteSegment): number {
  const shared = (a1: number, a2: number, b1: number, b2: number): number =>
    Math.max(
      0,
      Math.min(Math.max(a1, a2), Math.max(b1, b2)) - Math.max(Math.min(a1, a2), Math.min(b1, b2))
    )
  if (isHorizontal(a) && isHorizontal(b) && a.start.y === b.start.y) {
    return shared(a.start.x, a.end.x, b.start.x, b.end.x)
  }
  if (!isHorizontal(a) && !isHorizontal(b) && a.start.x === b.start.x) {
    return shared(a.start.y, a.end.y, b.start.y, b.end.y)
  }

  return 0
}

/**
 * A route's cost, and how many times it runs through a node. The two are kept apart because a
 * congested route (many crossings or shared lines) can cost as much as a collision without being
 * one: only a collision is a reason to search for a detour.
 *
 * Exported for canvases that generate their own candidate routes (the process canvas picks the
 * sides itself) and only want them ranked: `occupied` are the segments of the routes already
 * drawn, which a candidate should not cross or run along.
 */
export function scoreRoute(
  points: RoutePoint[],
  obstacles: RouteBox[],
  occupied: RouteSegment[],
  sourceBox: RouteBox | undefined,
  targetBox: RouteBox | undefined
): { score: number; collisions: number } {
  const segments = toSegments(points)
  let collisions = 0
  let conflicts = 0
  let length = 0
  segments.forEach((segment) => {
    const hits = (box: RouteBox | undefined): boolean =>
      !!box && segmentHitsBox(segment.start, segment.end, box)
    collisions += obstacles.filter(hits).length
    // A candidate can also leave its source, reverse, and cut straight back through it
    collisions += Number(hits(sourceBox)) + Number(hits(targetBox))
    occupied.forEach((other) => {
      conflicts +=
        overlapLength(segment, other) * OVERLAP_PENALTY +
        (crossingPoint(segment, other) ? CROSSING_PENALTY : 0)
    })
    length += segmentLength(segment)
  })
  const reversals = segments.slice(1).filter((segment, index) => {
    const previous = segments[index]
    const dx = Math.sign(previous.end.x - previous.start.x)
    const dy = Math.sign(previous.end.y - previous.start.y)

    return (
      (dx && dx === -Math.sign(segment.end.x - segment.start.x)) ||
      (dy && dy === -Math.sign(segment.end.y - segment.start.y))
    )
  }).length

  return {
    score:
      collisions * COLLISION_PENALTY +
      conflicts +
      length +
      Math.max(points.length - 2, 0) * BEND_PENALTY +
      reversals * REVERSAL_PENALTY,
    collisions,
  }
}

/**
 * Routes every edge. Short edges go first so they get the direct channels; long edges then detour
 * around them (the other way round gives spaghetti). Edges whose source or target is not among the
 * obstacles are still routed, with nothing to exclude for them. Self-loops are left out.
 *
 * Edges with a route in `keep` are not routed again: they keep that route, and it counts as
 * already drawn for the others, which spread around it as around any earlier edge. See
 * reusableRoutes for which routes can be kept.
 */
export function routeOrthogonal(
  edges: RoutingEdge[],
  obstacles: RoutingObstacle[],
  { keep = new Map() }: { keep?: EdgeRoutes } = {}
): EdgeRoutes {
  const boxOf = new Map(obstacles.map(({ id, box }) => [id, box]))
  const routable = edges.filter((edge) => edge.source !== edge.target)
  const kept = routable.filter(({ id }) => keep.has(id))
  const occupied: RouteSegment[] = kept.flatMap(({ id }) => toSegments(keep.get(id)))
  const routes = new Map<string, RoutePoint[]>(kept.map(({ id }) => [id, keep.get(id)]))
  const span = (edge: RoutingEdge): number => manhattan(edge.sourcePoint, edge.targetPoint)
  const ordered = routable
    .filter(({ id }) => !keep.has(id))
    .sort((a, b) => span(a) - span(b) || a.id.localeCompare(b.id))

  ordered.forEach((edge) => {
    const sourceBox = boxOf.get(edge.source)
    const targetBox = boxOf.get(edge.target)
    const blocking = obstacles
      .filter(({ id }) => id !== edge.source && id !== edge.target)
      .map(({ box }) => padBox(box, NODE_CLEARANCE))
    const { sourcePoint: source, targetPoint: target } = edge
    const sourceOut = moveOutward(source, edge.sourceSide, HANDLE_STUB)
    const targetOut = moveOutward(target, edge.targetSide, HANDLE_STUB)
    const conflicts = occupied.slice(-MAX_CONFLICT_SEGMENTS)
    const scored = candidateRoutes(
      source,
      sourceOut,
      targetOut,
      target,
      channelObstacles(blocking, sourceOut, targetOut)
    ).map((points) => ({
      points,
      ...scoreRoute(points, blocking, conflicts, sourceBox, targetBox),
    }))
    const cheapest = scored.reduce((best, candidate) =>
      candidate.score < best.score ? candidate : best
    )
    // No single-channel route clears every node: search a detour among the nodes around the edge
    // before accepting one through a node. A detour that runs through fewer nodes always wins; one
    // that runs through as many (it only sees the nodes near the edge, never other edges) is kept
    // only when it also costs less.
    const detour =
      cheapest.collisions > 0
        ? findRectilinearRoute(
            sourceOut,
            targetOut,
            [...obstaclesAround(blocking, sourceOut, targetOut), sourceBox, targetBox].filter(
              Boolean
            ),
            DETOUR_VISITS
          )
        : null
    const detourRoute = detour && simplifyRoute([source, ...detour, target])
    const detourCost =
      detourRoute && scoreRoute(detourRoute, blocking, conflicts, sourceBox, targetBox)
    const detourWins =
      !!detourCost &&
      (detourCost.collisions < cheapest.collisions ||
        (detourCost.collisions === cheapest.collisions && detourCost.score < cheapest.score))
    const best = detourWins ? detourRoute : cheapest.points
    occupied.push(...toSegments(best))
    routes.set(edge.id, best)
  })

  return routes
}

/**
 * The cheapest single-channel route from `start` to `end` clear of every obstacle, else an A*
 * search over the sparse grid of obstacle borders, which finds winding corridors through dense
 * drawings. Null when the two points are boxed in, or no route turns up within `maxVisits` grid
 * points (a boxed-in end otherwise makes the search visit the whole grid).
 */
export function findRectilinearRoute(
  start: RoutePoint,
  end: RoutePoint,
  obstacles: RouteBox[],
  maxVisits = Infinity
): RoutePoint[] | null {
  const inside = (point: RoutePoint, box: RouteBox): boolean =>
    point.x > box.left && point.x < box.right && point.y > box.top && point.y < box.bottom
  // An end inside a node (a node dropped against a handle) can never be reached: say so at once
  // instead of searching the whole grid for it
  if (obstacles.some((box) => inside(start, box) || inside(end, box))) {
    return null
  }
  const clear = (a: RoutePoint, b: RoutePoint): boolean =>
    !obstacles.some((box) => segmentHitsBox(a, b, box))
  const sortedUnique = (values: number[]): number[] => [...new Set(values)].sort((a, b) => a - b)
  const xs = sortedUnique([start.x, end.x, ...obstacles.flatMap((box) => [box.left, box.right])])
  const ys = sortedUnique([start.y, end.y, ...obstacles.flatMap((box) => [box.top, box.bottom])])
  const cost = (points: RoutePoint[]): number =>
    points.slice(1).reduce((sum, point, index) => sum + manhattan(points[index], point), 0) +
    points.length * 20

  const direct = [
    ...xs.map((x) => [start, { x, y: start.y }, { x, y: end.y }, end]),
    ...ys.map((y) => [start, { x: start.x, y }, { x: end.x, y }, end]),
  ]
    .map(simplifyRoute)
    .sort((a, b) => cost(a) - cost(b))
    .find((points) => points.slice(1).every((point, index) => clear(points[index], point)))
  if (direct) {
    return direct
  }

  const width = xs.length
  const pointOf = (id: number): RoutePoint => ({ x: xs[id % width], y: ys[Math.floor(id / width)] })
  const from = ys.indexOf(start.y) * width + xs.indexOf(start.x)
  const to = ys.indexOf(end.y) * width + xs.indexOf(end.x)
  const reachedAt = new Map<number, number>([[from, 0]])
  const previous = new Map<number, number>()
  const settled = new Set<number>()
  const open = new MinHeap()
  open.push(from, manhattan(start, end))

  /** Relaxes the grid neighbours of a settled point that a straight clear segment reaches. */
  const expand = (current: number): void => {
    const column = current % width
    const row = Math.floor(current / width)
    const point = pointOf(current)
    const neighbours = [
      column > 0 ? current - 1 : -1,
      column < width - 1 ? current + 1 : -1,
      row > 0 ? current - width : -1,
      row < ys.length - 1 ? current + width : -1,
    ]
    neighbours
      .filter((next) => next >= 0 && !settled.has(next))
      .forEach((next) => {
        const nextPoint = pointOf(next)
        const reached = reachedAt.get(current) + manhattan(point, nextPoint)
        if (reached >= (reachedAt.get(next) ?? Infinity) || !clear(point, nextPoint)) {
          return
        }
        reachedAt.set(next, reached)
        previous.set(next, current)
        open.push(next, reached + manhattan(nextPoint, end))
      })
  }

  while (open.size && settled.size < maxVisits) {
    const current = open.pop()
    if (current === to) {
      const route = [end]
      for (let id = to; id !== from;) {
        id = previous.get(id)
        route.push(pointOf(id))
      }

      return simplifyRoute(route.reverse())
    }
    // A point is pushed again whenever a cheaper way to it is found; only its first pop counts
    if (!settled.has(current)) {
      settled.add(current)
      expand(current)
    }
  }

  return null
}

/** Binary min-heap of grid ids by score, for the A* open set. */
class MinHeap {
  private readonly items: Array<{ id: number; score: number }> = []

  get size(): number {
    return this.items.length
  }

  push(id: number, score: number): void {
    const items = this.items
    let index = items.length
    items.push({ id, score })
    while (index > 0) {
      const parent = Math.floor((index - 1) / 2)
      if (items[parent].score <= score) {
        break
      }
      items[index] = items[parent]
      index = parent
    }
    items[index] = { id, score }
  }

  pop(): number {
    const items = this.items
    const top = items[0]
    const last = items.pop()
    if (items.length) {
      let index = 0
      while (index * 2 + 1 < items.length) {
        let child = index * 2 + 1
        if (child + 1 < items.length && items[child + 1].score < items[child].score) {
          child++
        }
        if (last.score <= items[child].score) {
          break
        }
        items[index] = items[child]
        index = child
      }
      items[index] = last
    }

    return top.id
  }
}
